import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  Modal,
  Paper,
  Button,
  TextField,
} from "@material-ui/core";
import { Autocomplete } from "@mui/material";
import "../App.css";
import "@material-ui/icons";
import Video from "./Video";
import "@material-ui/core/styles";
import { useVideos, findEventPageForVideoId, useVideoStore, getStartAfterDocForPage } from "../hooks/VideoData";
import { makeStyles } from "@material-ui/core/styles";
import { useParams } from "react-router-dom";
import 'firebase/firestore';
import firebase from 'firebase/compat/app';
import { FireSQL } from 'firesql';
import Favourite from "./Favourite";
import TopBar from "./TopBar";
import useScrollDirection from "../hooks/UseScrollDirection";

const useStyles = makeStyles((theme) => ({
  paper: {
    fontFamily: 'Harvest',
    width: 300,
    top: "50%",
    left: "50%",
    position: "absolute",
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
    transform: "translate(-50%, -50%)",
    backgroundColor: theme.palette.background.paper,
  },
  greenButton: {
    fontFamily: 'Harvest',
    backgroundColor: "#00af50",
    color: "#000000",
    "&:hover": {
      backgroundColor: "#008f40",
    },
  },
}));

function App() {
  const classes = useStyles();
  const { scrollDir, scrollCallback } = useScrollDirection();
  const { videos, page, setPage, searchVideo, getData, nextPage, start } = useVideos();
  const { videoId } = useParams();
  const [open, setOpen] = useState(false);
  const [endDate, setEndDate] = useState(null);
  const [endPrice, setEndPrice] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [startPrice, setStartPrice] = useState("");
  const [pageFraction, setPageFraction] = useState(0);
  const [bandName, setBandName] = useState("");
  const [location] = useState("");
  const [searchType, setSearchType] = useState("band"); // "band" or "venue"
  const [totalSize, setTotalSize] = useState(0);
  const [showFavourites, setShowFavourites] = useState(false);
  const [bandSuggestions, setBandSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const debounceTimerRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const rafRef = useRef(null);
  const prevActiveIndicesRef = useRef(new Set());
  const activeIndicesUpdateTimerRef = useRef(null);
  const { dbName: dbNameParam } = useParams();
  const resetVideos = useVideoStore(state => state.resetVideos);
  const [loadingSharedEvent, setLoadingSharedEvent] = useState(false);
  const [sharedEventGlobalIndex, setSharedEventGlobalIndex] = useState(null);
  const [sharedEventBaseIndex, setSharedEventBaseIndex] = useState(null);
  const [activeVideoIndices, setActiveVideoIndices] = useState(new Set([0]));
  // Debug state
  const [showDebug, setShowDebug] = useState(false); // Set to true to show debug overlay
  const debugInfoRef = useRef({
    currentPage: 0,
    scrollTop: 0,
    activeIndices: [],
    lastUpdate: null,
    updateCount: 0,
    scrollEventCount: 0,
    rafTime: null
  });
  const [debugInfo, setDebugInfo] = useState(debugInfoRef.current);

  useEffect(() => {
    let cancelled = false;
    async function loadSharedEvent() {
      if (!videoId) {
        if (videos.length === 0) {
          getData();
        }
        setLoadingSharedEvent(false);
        setSharedEventGlobalIndex(null);
        setSharedEventBaseIndex(null);
        return;
      }
      setLoadingSharedEvent(true);
      resetVideos();
      await new Promise(resolve => setTimeout(resolve, 0));
      const dbName = dbNameParam;
      const pageSize = 6;
      const result = await findEventPageForVideoId(videoId, dbName, pageSize);
      if (cancelled) return;
      if (!result) {
        setLoadingSharedEvent(false);
        setSharedEventGlobalIndex(null);
        setSharedEventBaseIndex(null);
        return;
      }
      const { pageNumber, count, indexInPage } = result;
      setSharedEventGlobalIndex(count);
      setSharedEventBaseIndex(count - indexInPage);
      let startAfterDoc = null;
      if (pageNumber > 0) {
        startAfterDoc = await getStartAfterDocForPage(dbName, pageNumber, pageSize);
      }
      if (cancelled) return;
      // Pass includePastEvents=true for shared links to ensure we load the correct page
      // even if the event is in the past, matching the position calculation in findEventPageForVideoId
      await getData(startAfterDoc, true);
      if (cancelled) return;
      setLoadingSharedEvent(false);
    }
    loadSharedEvent();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId]); // Other dependencies are stable functions from hooks or params

  useEffect(() => {
    if (!videoId || loadingSharedEvent) return;
    const idx = videos.findIndex(v => v.url === videoId || v.id === videoId);
    if (idx !== -1) {
      setPage(idx);
      setPageFraction(idx);
      const videoElement = document.querySelector('.app_videos');
      if (videoElement) {
        const videoHeight = videoElement.clientHeight;
        const scrollPosition = idx * videoHeight;
        videoElement.scrollTo({
          top: scrollPosition,
          behavior: 'auto'
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videos, videoId, loadingSharedEvent]); // setPage and setPageFraction are stable setters

  const handleSubmit = () => {
    // Determine if we're searching by band or venue based on searchType
    // If searchType is not explicitly set to 'venue', check if current input matches a venue suggestion
    let finalSearchType = searchType;
    if (searchType === 'band' && bandName) {
      // Check if the typed text matches a venue suggestion
      const matchingVenue = bandSuggestions.find(s => 
        typeof s !== 'string' && s.type === 'venue' && s.value.toLowerCase() === bandName.toLowerCase()
      );
      if (matchingVenue) {
        finalSearchType = 'venue';
      }
    }
    
    const searchParams = {
      bandName: finalSearchType === 'band' ? bandName : '',
      venueName: finalSearchType === 'venue' ? bandName : '', // Using bandName for the input value
      location,
      startPrice,
      endPrice,
      startDate,
      endDate,
      setTotalSizeCallback: setTotalSize, // Pass the setTotalSize function as a callback
    };
    // Reset page and scroll position before search
    setPage(0);
    setPageFraction(0);
    searchVideo(searchParams);
    setIsSearchActive(true); // Mark that a search is active
    
    setOpen(false);
  };

  const handleFavouriteClick = () => {
    setShowFavourites(!showFavourites);
  };
  
  const handleOpenSearch = useCallback(() => {
    setOpen(true);
  }, []);

  const handleClear = (event) => {
    event.preventDefault();
    handleClearSearch();
  };

  const handleClearSearch = () => {
    // Clear search state first
    setOpen(false);
    setEndDate("");
    setEndPrice("");
    setBandName("");
    setSearchType("band");
    setStartDate("");
    setStartPrice("");
    setBandSuggestions([]);
    setIsSearchActive(false); // Clear search active state
    
    // Clear videos and reset pagination to force reload of all events
    resetVideos();
    setPage(0);
    setPageFraction(0);
    
    // Load all videos from the database
    getData();
  };

  // Function to fetch matching band names and venues from database
  const fetchBandSuggestions = useCallback(async (inputValue) => {
    if (!dbNameParam || !inputValue || inputValue.trim().length < 2) {
      setBandSuggestions([]);
      return;
    }

    setLoadingSuggestions(true);
    try {
      const dbRef = firebase.firestore();
      const fireSQL = new FireSQL(dbRef);
      
      // Convert input to title case for better matching
      const words = inputValue.trim().toLowerCase().split(/\s+/);
      const titleCaseWords = words.map(word => word.charAt(0).toUpperCase() + word.slice(1));
      const searchTerm = titleCaseWords.join(' ');
      
      // Escape single quotes in the input to prevent SQL injection
      const escapedInput = searchTerm.replace(/'/g, "''");
      
      // Query for bands that start with the input
      const bandQuery = `SELECT band FROM ${dbNameParam} WHERE (band LIKE '${escapedInput}%' OR band LIKE 'The ${escapedInput}%') ORDER BY band LIMIT 30`;
      
      // Query for venues - FireSQL only supports "value%" (starts with) pattern
      // Query venues starting with the search term, and also query a broader set
      // to find venues containing the term (like "Bristol Strange Brew" for "strange")
      const inputLower = escapedInput.toLowerCase();
      const inputUpper = escapedInput.toUpperCase();
      
      // Query 1: Venues starting with the search term
      const venueQueryExact = `SELECT venue FROM ${dbNameParam} WHERE (
        venue LIKE '${escapedInput}%' OR 
        venue LIKE '${inputLower}%' OR 
        venue LIKE '${inputUpper}%'
      ) ORDER BY venue LIMIT 100`;
      
      // Query 2: Get a sample of all venues (to find ones containing the term)
      // Limit to reasonable number to avoid performance issues
      const venueQueryAll = `SELECT venue FROM ${dbNameParam} ORDER BY venue LIMIT 500`;
      
      console.log('Autocomplete venue queries:', { exact: venueQueryExact, all: venueQueryAll });
      
      // Execute queries in parallel - get exact matches and broader set for filtering
      const [bandResults, venueResultsExact, venueResultsAll] = await Promise.all([
        fireSQL.query(bandQuery).catch((err) => {
          console.error('Band autocomplete query error:', err);
          return [];
        }),
        fireSQL.query(venueQueryExact).catch((err) => {
          console.error('Venue exact query error:', err);
          return [];
        }),
        // Only query all venues if search term is 3+ characters (to avoid performance issues)
        inputValue.trim().length >= 3 
          ? fireSQL.query(venueQueryAll).catch((err) => {
              console.error('Venue all query error:', err);
              return [];
            })
          : Promise.resolve([])
      ]);
      
      // Combine exact matches with broader results
      const allVenueResults = [...venueResultsExact, ...venueResultsAll];
      console.log('Autocomplete results - Bands:', bandResults.length, 'Venues (before filtering):', allVenueResults.length);
      
      // Filter venues client-side to find ones containing the search term (case-insensitive)
      // This handles cases like "Bristol Strange Brew" when searching for "strange"
      const searchTermLower = inputValue.toLowerCase().trim();
      const filteredVenues = allVenueResults
        .map(doc => doc.venue)
        .filter(Boolean)
        .filter(venue => venue.toLowerCase().includes(searchTermLower));
      
      // Extract unique values, prioritizing exact matches
      const uniqueBands = [...new Set(bandResults.map(doc => doc.band).filter(Boolean))].slice(0, 5);
      const uniqueVenues = [...new Set(filteredVenues)].slice(0, 5);
      
      console.log('Unique venues found:', uniqueVenues);
      
      // Create structured suggestions with type indicators
      const suggestions = [
        ...uniqueBands.map(band => ({ value: band, label: band, type: 'band' })),
        ...uniqueVenues.map(venue => ({ value: venue, label: venue, type: 'venue' }))
      ];
      
      setBandSuggestions(suggestions);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      setBandSuggestions([]);
    } finally {
      setLoadingSuggestions(false);
    }
  }, [dbNameParam]);

  // Debounced effect for band name suggestions
  useEffect(() => {
    // Clear previous timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Only fetch suggestions when modal is open and input is provided
    if (open && bandName) {
      debounceTimerRef.current = setTimeout(() => {
        fetchBandSuggestions(bandName);
      }, 300); // 300ms debounce
    } else {
      setBandSuggestions([]);
    }

    // Cleanup
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [bandName, open, fetchBandSuggestions]); // fetchBandSuggestions is memoized with useCallback

  useEffect(() => {
    const getTotal = async () => {
      if (!dbNameParam) {
        console.log('dbNameParam is not set!');
        setTotalSize(0);
        return;
      }

      // Only calculate total when not in search mode (show all events)
      if (isSearchActive) {
        return;
      }

      // Set totalSize to match the paginated query
      // For shared links (when videoId exists), we need to use ALL events to match
      // the position calculation in findEventPageForVideoId
      let totalQuery;
      if (dbNameParam.toLowerCase() === dbNameParam) {
        // Lowercase: use ALL events if viewing a shared link, otherwise future events only
        if (videoId) {
          // Shared link: use ALL events to match position calculation
          totalQuery = firebase.firestore().collection(dbNameParam);
        } else {
          // Normal browsing: future events only
          const timeNow = new Date().getTime() / 1000;
          totalQuery = firebase.firestore().collection(dbNameParam).where("timestamp", ">=", timeNow);
        }
      } else {
        // Mixed case: all events
        totalQuery = firebase.firestore().collection(dbNameParam);
      }
      const totalSnapshot = await totalQuery.get();
      setTotalSize(totalSnapshot.size);
      console.log('Set totalSize:', totalSnapshot.size, 'for dbName:', dbNameParam, 'videoId:', videoId ? 'present' : 'none');
    };

    getTotal();
  }, [dbNameParam, isSearchActive, videoId]);
  
  // Infinite/paginated loading for city list when there is no videoId and not loading a shared event
  useEffect(() => {
    if (videoId || loadingSharedEvent) return; // Only for city list, not shared links or while loading shared event
    if (videos.length <= 3) return;
    if (page > videos.length - 3 && start) {
      nextPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, videos, start, videoId, loadingSharedEvent]); // nextPage is stable from useVideos hook

  // Passive scroll event listener with requestAnimationFrame optimization
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;
    
    const handleScroll = () => {
      // Skip scroll calculation if we're resetting after search
      if (searchResetRef.current) {
        return;
      }
      
      // Cancel any pending animation frame
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }

      // Use requestAnimationFrame for smooth performance
      const rafStartTime = performance.now();
      rafRef.current = requestAnimationFrame(() => {
        if (!scrollContainer || searchResetRef.current) return;
        const scrollTop = scrollContainer.scrollTop;
        const clientHeight = scrollContainer.clientHeight;
        const rafEndTime = performance.now();
        const rafDuration = rafEndTime - rafStartTime;
        
        scrollCallback(scrollTop);
        const f = scrollTop / clientHeight;
        const newPage = Math.round(f);
        setPageFraction(newPage);
        if (newPage !== page) setPage(newPage);
        
        // Track active videos (current + 5 before/after for smoother scrolling)
        // Extended buffer prevents pauses when scrolling - ReactPlayer stays mounted in buffer
        // Only unmounts when far outside buffer range
        const activeSet = new Set();
        const startIdx = Math.max(0, newPage - 5);
        const endIdx = Math.min(videos.length - 1, newPage + 5);
        for (let i = startIdx; i <= endIdx; i++) {
          activeSet.add(i);
        }
        
        // Efficient comparison: check if sets are different (much faster than Array.from().sort().join())
        const prevSet = prevActiveIndicesRef.current;
        let setsDiffer = prevSet.size !== activeSet.size;
        if (!setsDiffer) {
          // Only check contents if sizes match
          for (const idx of activeSet) {
            if (!prevSet.has(idx)) {
              setsDiffer = true;
              break;
            }
          }
        }
        
        // Update debug info (throttled to reduce re-renders)
        const now = Date.now();
        debugInfoRef.current = {
          currentPage: newPage,
          scrollTop: Math.round(scrollTop),
          activeIndices: Array.from(activeSet).sort((a, b) => a - b),
          lastUpdate: now,
          updateCount: debugInfoRef.current.updateCount + (setsDiffer ? 1 : 0),
          scrollEventCount: debugInfoRef.current.scrollEventCount + 1,
          rafTime: rafDuration.toFixed(2)
        };
        // Only update debug state every 100ms to reduce re-renders
        if (!debugInfoRef.current.lastDebugUpdate || now - debugInfoRef.current.lastDebugUpdate > 100) {
          debugInfoRef.current.lastDebugUpdate = now;
          setDebugInfo({ ...debugInfoRef.current });
        }
        
        // Update state immediately - Set comparison already prevents unnecessary updates
        // Removed debounce to eliminate pause when starting to scroll after pausing
        if (setsDiffer) {
          prevActiveIndicesRef.current = activeSet;
          // Clear any pending update (in case cleanup didn't run)
          if (activeIndicesUpdateTimerRef.current) {
            clearTimeout(activeIndicesUpdateTimerRef.current);
            activeIndicesUpdateTimerRef.current = null;
          }
          setActiveVideoIndices(activeSet);
        }
      });
    };

    // Add passive scroll listener
    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
      if (activeIndicesUpdateTimerRef.current) {
        clearTimeout(activeIndicesUpdateTimerRef.current);
      }
      scrollContainer.removeEventListener('scroll', handleScroll);
    };
  }, [scrollCallback, page, setPage, videos.length]);

  // Initialize active video indices when videos load or page changes
  useEffect(() => {
    if (videos.length === 0) return;
    const activeSet = new Set();
    const currentPage = pageFraction >= 0 ? pageFraction : 0;
    // Extended buffer of 5 on each side for smoother scrolling
    for (let i = Math.max(0, currentPage - 5); i <= Math.min(videos.length - 1, currentPage + 5); i++) {
      activeSet.add(i);
    }
    prevActiveIndicesRef.current = activeSet;
    setActiveVideoIndices(activeSet);
  }, [videos.length, pageFraction]);

  // Reset scroll position and pageFraction when search results load
  const searchResetRef = useRef(false);
  useEffect(() => {
    if (isSearchActive && videos.length > 0) {
      // Reset immediately
      setPageFraction(0);
      searchResetRef.current = true;
      
      // Reset scroll position after DOM updates
      const timeoutId = setTimeout(() => {
        const scrollContainer = scrollContainerRef.current;
        if (scrollContainer) {
          scrollContainer.scrollTop = 0; // Direct assignment for immediate effect
          scrollContainer.scrollTo({
            top: 0,
            behavior: 'auto'
          });
        }
        // Clear the reset flag after a short delay
        setTimeout(() => {
          searchResetRef.current = false;
        }, 500);
      }, 50);
      
      return () => clearTimeout(timeoutId);
    } else {
      searchResetRef.current = false;
    }
  }, [isSearchActive, videos.length]);

  // Note: Browser back button navigation is handled by React Router
  // To clear search results, use the "Back to All Events" button

  return (
    <div className="app">
    {!showFavourites && (
    <>
    <TopBar 
      onOpenSearch={handleOpenSearch} 
      onFavouriteClick={handleFavouriteClick}
      isSearchActive={isSearchActive}
      onClearSearch={handleClearSearch}
    />
      <div
        ref={scrollContainerRef}
        className="app_videos"
      >
        {videos.map((v, i) => {
          // Use a unique key that includes the index to prevent duplicate key warnings
          const uniqueKey = v.id ? `${isSearchActive ? 'search-' : ''}${v.id}-${i}` : 
                           v.url ? `${isSearchActive ? 'search-' : ''}${v.url}-${i}` : 
                           `${isSearchActive ? 'search-' : ''}video-${i}`;
          const isActive = activeVideoIndices.has(i);
          return (
            <Video
              key={uniqueKey}
              index={i}
              page={pageFraction}
              scrollDir={scrollDir}
              item={v}
              isActive={isActive}
              {...v}
            />
          );
        })}
      </div>
      </>
      )}
      {showFavourites && (<Favourite setShowFavourites={setShowFavourites} />)} 
      <div className="debug">
        {Number.isFinite(totalSize) && totalSize > 0 ? (
          <>
            {videoId && Number.isFinite(sharedEventBaseIndex) && Number.isFinite(page) ? (
              <>Events : {sharedEventBaseIndex + page + 1} / {totalSize}</>
            ) : videoId && Number.isFinite(sharedEventGlobalIndex) ? (
              <>Events : {sharedEventGlobalIndex + 1} / {totalSize}</>
            ) : Number.isFinite(page) ? (
              <>Events : {page >= totalSize ? page - (totalSize * (Math.floor(page/totalSize))) + 1 : page + 1 } / {totalSize}</>
            ) : (
              <>Events: Loading...</>
            )}
          </>
        ) : (
          <>Events: Loading...</>
        )}
      </div>
      
      {/* Debug overlay for scrolling issues */}
      {showDebug && (
      <div style={{
        position: 'fixed',
        top: '10px',
        left: '10px',
        right: '10px',
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        color: '#00ff00',
        padding: '10px',
        fontSize: '11px',
        fontFamily: 'monospace',
        zIndex: 10000,
        borderRadius: '5px',
        border: '1px solid #00ff00',
        maxHeight: '50vh',
        overflow: 'auto',
        lineHeight: '1.4'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
          <div style={{ fontWeight: 'bold', color: '#ffff00' }}>🔍 SCROLL DEBUG</div>
          <button 
            onClick={() => setShowDebug(false)}
            style={{
              background: '#ff0000',
              color: 'white',
              border: 'none',
              padding: '3px 8px',
              borderRadius: '3px',
              cursor: 'pointer',
              fontSize: '10px'
            }}
          >
            ✕ Close
          </button>
        </div>
        <div>Page: {debugInfo.currentPage} | Scroll: {debugInfo.scrollTop}px</div>
        <div>Active: [{debugInfo.activeIndices.join(', ')}] ({debugInfo.activeIndices.length} players)</div>
        <div>Updates: {debugInfo.updateCount} | Events: {debugInfo.scrollEventCount}</div>
        <div>RAF Time: {debugInfo.rafTime}ms {debugInfo.rafTime && parseFloat(debugInfo.rafTime) > 16 ? '⚠️' : '✓'}</div>
        <div>Last Update: {debugInfo.lastUpdate ? new Date(debugInfo.lastUpdate).toLocaleTimeString() : 'Never'}</div>
        <div style={{ marginTop: '5px', fontSize: '10px', color: '#888' }}>
          Buffer: ±5 | Total Videos: {videos.length} | Page Fraction: {pageFraction}
        </div>
        {debugInfo.rafTime && parseFloat(debugInfo.rafTime) > 16 && (
          <div style={{ color: '#ff0000', marginTop: '5px', fontWeight: 'bold' }}>
            ⚠️ SLOW RAF: {debugInfo.rafTime}ms (target: &lt;16ms for 60fps)
          </div>
        )}
        <div style={{ marginTop: '5px', fontSize: '9px', color: '#666' }}>
          Mounted: Videos within ±5 of current page have ReactPlayer mounted
        </div>
      </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)}>
        <Paper className={classes.paper}>
          <div className="modal">
            <h2>Filter Video</h2>
            <br />
            <Autocomplete
              freeSolo
              options={bandSuggestions}
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                return option.label || option.value || option;
              }}
              groupBy={(option) => {
                if (typeof option === 'string') return '';
                return option.type === 'band' ? 'Bands' : option.type === 'venue' ? 'Venues' : '';
              }}
              inputValue={bandName}
              onInputChange={(event, newInputValue, reason) => {
                if (reason !== 'reset') {
                  setBandName(newInputValue);
                  setSearchType("band"); // Default to band search when typing
                }
              }}
              onChange={(event, newValue) => {
                if (newValue) {
                  if (typeof newValue === 'string') {
                    setBandName(newValue);
                    setSearchType("band");
                  } else {
                    setBandName(newValue.value || newValue.label || "");
                    setSearchType(newValue.type || "band");
                  }
                } else {
                  setBandName("");
                  setSearchType("band");
                }
              }}
              loading={loadingSuggestions}
              renderOption={(props, option) => {
                const label = typeof option === 'string' ? option : option.label || option.value || option;
                const type = typeof option === 'string' ? 'band' : option.type || 'band';
                return (
                  <li {...props} key={`${type}-${label}`}>
                    <span style={{ fontWeight: 'bold', marginRight: '8px' }}>
                      {type === 'band' ? '🎵' : '📍'}
                    </span>
                    {label}
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Band Name or Venue"
                  fullWidth
                  variant="standard"
                />
              )}
              noOptionsText={bandName && bandName.trim().length >= 2 ? "No bands or venues found" : "Type at least 2 characters..."}
              loadingText="Searching..."
            />
            <br />
            <br />
            <div style={{ display: "flex" }}>
              <TextField
                fullWidth
                type={"date"}
                value={startDate}
                style={{ marginRight: 5 }}
                onChange={(event) => setStartDate(event.target.value)}
              />
              <TextField
                fullWidth
                type={"date"}
                value={endDate}
                style={{ marginLeft: 5 }}
                onChange={(event) => setEndDate(event.target.value)}
              />
            </div>
            <br />
            <br />
            <div style={{ display: "flex" }}>
              <Button
                fullWidth
                className={classes.greenButton}
                variant="contained"
                onClick={handleClear}
                style={{ marginRight: 5 }}
              >
                Cancel
              </Button>
              <Button
                className={classes.greenButton}
                fullWidth
                variant="contained"
                onClick={handleSubmit}
                style={{ marginLeft: 5 }}
              >
                Search
              </Button>
            </div>
          </div>
        </Paper>
      </Modal>
    </div>
  );
}

export default App;
